type Identity = {
    user_id: string | number
    user_hash: string
} & Record<string, unknown>

export function useIntercomHelpers() {
    const { $intercom } = useNuxtApp();
    const { status, data: session } = useAuth();
    const { public: { supportUrl } } = useRuntimeConfig();

    // Whether the site still has a support channel to offer, and what it is. With Intercom off, a
    // control that calls show() does nothing at all, so callers need to know to point at the
    // contact form instead, or to drop the control entirely rather than leave a dead one.
    const isIntercomEnabled = computed(() => $intercom?.isEnabled?.() ?? false);
    const supportLink = computed(() => supportUrl || null);
    const hasSupportChannel = computed(() => isIntercomEnabled.value || !!supportLink.value);

    // What a "contact support" anchor should carry. With Intercom on the click is intercepted and
    // the href is inert; with it off this is a real destination, so the link behaves like a link:
    // visible target, middle-click, open in new tab.
    const supportHref = computed(() => isIntercomEnabled.value ? '#' : (supportLink.value ?? '#'));

    const buildIdentity = (): Identity | null => {
        if(status.value !== 'authenticated') {
            return null;
        }

        const u: any = session.value?.data ?? {};
        const user_id = u?.intercom_id;
        const user_hash = u?.intercom_hash;

        if(!user_id || !user_hash) {
            return null;
        }

        return { user_id, user_hash };
    }

    const attachLoggedUser = async () => {
        if(!import.meta.client || !isIntercomEnabled.value) {
            return
        }

        await $intercom.onceReady();
        const id = buildIdentity();

        if(id) {
            $intercom.identify(id);
        }
    }

    const showIntercom = async () => {
        if(!isIntercomEnabled.value) {
            return;
        }

        await $intercom.onceReady();
        $intercom.show();
    }

    /**
     * For controls with no href of their own, such as a <button>. Opens the messenger while
     * Intercom is on, otherwise navigates to the contact form.
     */
    const openSupport = (): boolean => {
        if(isIntercomEnabled.value) {
            showIntercom();
            return true;
        }

        if(supportLink.value && import.meta.client) {
            window.location.href = supportLink.value;
            return true;
        }

        return false;
    }

    /**
     * For anchors bound to supportHref. With Intercom on the href is the inert '#', so the click
     * has to be cancelled and handed to the messenger; with it off the href is the real contact
     * form and the browser is left to follow it, which keeps middle-click and open-in-new-tab
     * working. Deliberately separate from openSupport(): an anchor that cancelled its own
     * navigation and then set window.location would break both.
     */
    const onSupportLinkClick = (event: Event): void => {
        if(isIntercomEnabled.value) {
            event.preventDefault();
            showIntercom();
            return;
        }

        if(!supportLink.value) {
            event.preventDefault();
        }
    }

    return {
        attachLoggedUser,
        showIntercom,
        openSupport,
        onSupportLinkClick,
        isIntercomEnabled,
        supportLink,
        supportHref,
        hasSupportChannel,
    }
}
